import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, Building2, Shield, CheckCircle, XCircle, 
  Clock, Edit2, Save, X, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ProfileSection = () => {
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [showEmailOTP, setShowEmailOTP] = useState(false);
  const [emailOTP, setEmailOTP] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || ''
      });
    }
    fetchKYCStatus();
  }, [user]);

  const fetchKYCStatus = async () => {
    try {
      // Check cache first (5 minute TTL)
      const cached = localStorage.getItem('kyc_status_cache');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        if (age < 5 * 60 * 1000) { // 5 minutes
          setKycStatus(data);
          // Still fetch in background to update cache
          fetchKYCStatusFromAPI();
          return;
        }
      }

      // If no cache or expired, fetch from API
      await fetchKYCStatusFromAPI();
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    }
  };

  const fetchKYCStatusFromAPI = async () => {
    try {
      const response = await api.get('/kyc/status');
      setKycStatus(response.data);
      
      // Cache the result
      localStorage.setItem('kyc_status_cache', JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error fetching KYC status from API:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await api.put('/profile/', profileData);
      
      if (updateUser) {
        updateUser(response.data);
      }
      
      setIsEditing(false);
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setProfileData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      email: user.email || ''
    });
    setIsEditing(false);
  };

  const handleSendEmailVerification = async () => {
    setLoading(true);
    try {
      await api.post('/auth/send-verification-email');
      setShowEmailOTP(true);
      toast({
        title: "Verification Email Sent",
        description: "Please check your email inbox and enter the OTP code below.",
      });
    } catch (error) {
      console.error('Error sending verification email:', error);
      toast({
        title: "Failed to Send Email",
        description: error.response?.data?.detail || "Could not send verification email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailOTP = async () => {
    if (!emailOTP || emailOTP.length !== 6) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a valid 6-digit OTP code.",
        variant: "destructive",
      });
      return;
    }

    setVerifyingEmail(true);
    try {
      await api.post(`/auth/verify-email?otp_code=${emailOTP}`);
      
      // Fetch updated user data from server
      const userResponse = await api.get('/auth/me');
      
      // Update user context with fresh data
      if (updateUser) {
        updateUser(userResponse.data);
      }
      
      setShowEmailOTP(false);
      setEmailOTP('');
      toast({
        title: "Email Verified",
        description: "Your email has been verified successfully!",
      });
    } catch (error) {
      console.error('Error verifying email:', error);
      toast({
        title: "Verification Failed",
        description: error.response?.data?.detail || "Invalid or expired OTP code.",
        variant: "destructive",
      });
    } finally {
      setVerifyingEmail(false);
    }
  };

  const calculateProfileCompletion = () => {
    let completed = 0;
    let total = 6;
    
    if (user?.first_name) completed++;
    if (user?.last_name) completed++;
    if (user?.email) completed++;
    if (user?.phone) completed++;
    if (user?.is_email_verified) completed++;
    if (kycStatus?.status === 'verified') completed++;
    
    return Math.round((completed / total) * 100);
  };

  const getKYCStatusConfig = () => {
    switch (kycStatus?.status) {
      case 'verified':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Verified',
          badgeClass: 'bg-green-100 text-green-800'
        };
      case 'pending':
      case 'under_review':
        return {
          icon: Clock,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          label: 'Pending Review',
          badgeClass: 'bg-orange-100 text-orange-800'
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Rejected',
          badgeClass: 'bg-red-100 text-red-800'
        };
      default:
        return {
          icon: AlertCircle,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: 'Not Verified',
          badgeClass: 'bg-gray-100 text-gray-800'
        };
    }
  };

  const kycConfig = getKYCStatusConfig();
  const KYCIcon = kycConfig.icon;
  const profileCompletion = calculateProfileCompletion();
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();

  return (
    <div className="space-y-6">
      {/* Header with Edit Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
          <p className="text-gray-600 mt-1">Manage your account information and preferences</p>
        </div>
        {!isEditing ? (
          <Button
            onClick={() => setIsEditing(true)}
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              onClick={handleCancel}
              variant="outline"
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>

      {/* Main Profile Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Profile Overview */}
        <div className="space-y-6">
          {/* Profile Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-orange-500" />
                Profile Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar Section */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {initials || <User className="w-10 h-10" />}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {user?.first_name} {user?.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">{user?.email}</p>
                  <Badge className="mt-2 bg-orange-100 text-orange-800">
                    {user?.user_type === 'business' ? 'Business Account' : 'Individual Account'}
                  </Badge>
                </div>
              </div>

              {/* Profile Completion */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Profile Completion</span>
                  <span className="text-sm font-bold text-orange-600">{profileCompletion}%</span>
                </div>
                <Progress value={profileCompletion} className="h-2" />
                <p className="text-xs text-gray-500 mt-2">
                  {profileCompletion === 100 
                    ? 'Your profile is complete!' 
                    : 'Complete your profile to unlock all features'}
                </p>
              </div>

              {/* Verification Status */}
              <div className="space-y-3 pt-4 border-t">
                <h4 className="text-sm font-semibold text-gray-700">Verification Status</h4>
                
                {/* Email Verification */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Email</span>
                    </div>
                    {user?.is_email_verified ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Not Verified
                      </Badge>
                    )}
                  </div>
                  
                  {!user?.is_email_verified && !showEmailOTP && (
                    <Button
                      size="sm"
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={handleSendEmailVerification}
                      disabled={loading}
                    >
                      <Mail className="w-3 h-3 mr-2" />
                      {loading ? 'Sending...' : 'Verify Email'}
                    </Button>
                  )}
                  
                  {!user?.is_email_verified && showEmailOTP && (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-gray-600">
                        Enter the 6-digit code sent to your email
                      </p>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Enter OTP"
                          value={emailOTP}
                          onChange={(e) => setEmailOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                          className="flex-1"
                        />
                        <Button
                          size="sm"
                          onClick={handleVerifyEmailOTP}
                          disabled={verifyingEmail || emailOTP.length !== 6}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                        >
                          {verifyingEmail ? 'Verifying...' : 'Submit'}
                        </Button>
                      </div>
                      <button
                        onClick={handleSendEmailVerification}
                        disabled={loading}
                        className="text-xs text-orange-600 hover:text-orange-700 underline"
                      >
                        Resend OTP
                      </button>
                    </div>
                  )}
                </div>

                {/* Phone Verification */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Phone</span>
                    </div>
                    {user?.is_phone_verified ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Not Verified
                      </Badge>
                    )}
                  </div>
                  {!user?.is_phone_verified && (
                    <Button
                      size="sm"
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={() => {
                        toast({
                          title: "Verification SMS Sent",
                          description: "Please check your phone for the verification code.",
                        });
                      }}
                    >
                      <Phone className="w-3 h-3 mr-2" />
                      Verify Phone
                    </Button>
                  )}
                </div>

                {/* KYC Verification */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">KYC</span>
                    </div>
                    <Badge className={kycConfig.badgeClass}>
                      <KYCIcon className="w-3 h-3 mr-1" />
                      {kycConfig.label}
                    </Badge>
                  </div>
                  {kycStatus?.status !== 'verified' && (
                    <Button
                      size="sm"
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={() => window.location.href = '/profile/kyc'}
                    >
                      <Shield className="w-3 h-3 mr-2" />
                      {kycStatus?.status === 'rejected' ? 'Resubmit KYC' : 
                       kycStatus?.status === 'pending' ? 'View KYC Status' : 
                       'Complete KYC'}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Editable Information */}
        <div className="space-y-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-orange-500" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  {isEditing ? (
                    <Input
                      value={profileData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      placeholder="Enter first name"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 rounded-md text-gray-900">
                      {user?.first_name || 'Not set'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  {isEditing ? (
                    <Input
                      value={profileData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      placeholder="Enter last name"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 rounded-md text-gray-900">
                      {user?.last_name || 'Not set'}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Input
                    value={profileData.email}
                    disabled
                    className="bg-gray-50 cursor-not-allowed"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {user?.is_email_verified ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Email cannot be changed. Contact support if needed.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                {isEditing ? (
                  <Input
                    value={profileData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                    type="tel"
                  />
                ) : (
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md">
                    <span className="text-gray-900">{user?.phone || 'Not set'}</span>
                    {user?.is_phone_verified ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Type
                </label>
                <div className="px-3 py-2 bg-gray-50 rounded-md text-gray-900 flex items-center gap-2">
                  {user?.user_type === 'business' ? (
                    <>
                      <Building2 className="w-4 h-4 text-orange-600" />
                      <span>Business Account</span>
                    </>
                  ) : (
                    <>
                      <User className="w-4 h-4 text-orange-600" />
                      <span>Individual Account</span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KYC Verification Card */}
          <Card className={`border-2 ${kycConfig.borderColor}`}>
            <CardHeader className={kycConfig.bgColor}>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className={`w-5 h-5 ${kycConfig.color}`} />
                  <span>KYC Verification</span>
                </div>
                <Badge className={kycConfig.badgeClass}>
                  <KYCIcon className="w-3 h-3 mr-1" />
                  {kycConfig.label}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  {kycStatus?.status === 'verified' 
                    ? 'Your KYC is verified. You have access to all features including wallet and COD payments.'
                    : kycStatus?.status === 'pending'
                    ? 'Your KYC documents are under review. We will notify you once verified.'
                    : kycStatus?.status === 'rejected'
                    ? 'Your KYC was rejected. Please resubmit with correct documents.'
                    : 'Complete your KYC verification to unlock wallet usage and COD payment options.'}
                </p>

                {kycStatus && (
                  <div className="grid grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <div className={`text-xs font-medium mb-1 ${kycStatus.can_book_shipment ? 'text-green-600' : 'text-gray-400'}`}>
                        Booking
                      </div>
                      {kycStatus.can_book_shipment ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400 mx-auto" />
                      )}
                    </div>
                    <div className="text-center">
                      <div className={`text-xs font-medium mb-1 ${kycStatus.can_use_wallet ? 'text-green-600' : 'text-gray-400'}`}>
                        Wallet
                      </div>
                      {kycStatus.can_use_wallet ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400 mx-auto" />
                      )}
                    </div>
                    <div className="text-center">
                      <div className={`text-xs font-medium mb-1 ${kycStatus.can_use_cod ? 'text-green-600' : 'text-gray-400'}`}>
                        COD
                      </div>
                      {kycStatus.can_use_cod ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400 mx-auto" />
                      )}
                    </div>
                  </div>
                )}

                {kycStatus?.status !== 'verified' && (
                  <Button
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    onClick={() => window.location.href = '/profile/kyc'}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    {kycStatus?.status === 'rejected' ? 'Resubmit KYC' : 'Complete KYC Verification'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;
